import React, { useState } from "react";
import {
  Modal,
  Box,
  SpaceBetween,
  Button,
  Icon,
  FormField,
  Input,
  Alert,
  IconProps,
} from "@cloudscape-design/components";

interface DeleteConfirmationModalProps {
  isVisible: boolean;
  isDeleting: boolean;
  onDismiss: () => void;
  onConfirm: () => Promise<void>;
  headerIconName?: IconProps.Name;
  headerText?: string;
  mainButtonText?: string;
  mainButtonLoadingText?: string;
  children?: React.ReactNode;
  deleteConfirmationValue: string;
  deleteAction?: string;
  showAlertBox?: boolean;
  alertBoxTitle?: string;
  alertBoxContent?: React.ReactElement;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isVisible,
  onDismiss,
  onConfirm,
  isDeleting,
  headerText = "Delete",
  headerIconName = "remove",
  mainButtonText = "Delete",
  mainButtonLoadingText = "Deleting...",
  children,
  deleteConfirmationValue,
  deleteAction = "deletion",
  showAlertBox,
  alertBoxTitle,
  alertBoxContent
}) => {
  const [deletionValue, setDeletionValue] = useState("");

  const handleDismiss = () => {
    if (isDeleting) return;
    setDeletionValue("");
    onDismiss();
  };

  const handleConfirm = async () => {
    try {
      await onConfirm();
      
    } catch (error) { 
      console.error("Error during deletion:", error);
      } 
    finally {
      handleDismiss();
    }
  };

  return (
    <Modal
      onDismiss={() => {
        handleDismiss();
      }}
      visible={isVisible}
      closeAriaLabel="Close modal"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              className={`${
                deleteConfirmationValue !== deletionValue || isDeleting
                  ? "disabled-delete-button"
                  : "delete-button"
              }`}
              variant={"primary"}
              onClick={handleConfirm}
              loading={isDeleting}
              disabled={deleteConfirmationValue !== deletionValue || isDeleting}
            >
              {isDeleting ? mainButtonLoadingText : mainButtonText}
            </Button>
            <Button
              variant="link"
              onClick={handleDismiss}
              disabled={isDeleting}
            >
              Cancel
            </Button>
          </SpaceBetween>
        </Box>
      }
      header={
        <SpaceBetween direction="horizontal" alignItems="center" size="xs">
          <Icon name={headerIconName} variant="error" />
          <Box variant="h3">{headerText}</Box>
        </SpaceBetween>
      }
    >
      <SpaceBetween size="xxl">
        <SpaceBetween size={"s"}>
          <div>{children}</div>

          <FormField
            description={
              <React.Fragment>
                To confirm {deleteAction} please type "{deleteConfirmationValue}"
              </React.Fragment>
            }
          >
            <Input
              value={deletionValue}
              onChange={({ detail }) => setDeletionValue(detail.value)}
              placeholder={deleteConfirmationValue}
              autoComplete="off"
            />
          </FormField>
        </SpaceBetween>

        {showAlertBox && <Alert type="warning" header={alertBoxTitle}>
          <Box variant="p">{alertBoxContent}</Box>
        </Alert>}
      </SpaceBetween>
    </Modal>
  );
};

export default DeleteConfirmationModal;
